import { useCallback, useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

/**
 * Props for the useApplyArticlePrices hook
 * @interface UseApplyArticlesProps
 * @property {Position[]} positions - Array of positions to be enriched with article prices
 * @property {ErpConversionGroupByType} groupByType - Group by type
 */
type UseApplyArticlesProps<T extends Position> = {
  positions: T[];
  groupByType?: string;
  sourcePrices?: boolean;
};

/**
 * Custom hook that enriches position data with article prices and discounts
 * @function useApplyArticlePricesOnPositions
 * @param {UseApplyArticlesProps<T>} props - The hook props
 * @returns {Position[]} - The enriched positions with article prices and discounts applied
 *
 * @example
 * const enrichedPositions = useApplyArticlePricesOnPositions({ positions: myPositions });
 */
export const useApplyArticlePricesOnPositions = <T extends Position>({
  positions,
  groupByType,
  sourcePrices,
}: UseApplyArticlesProps<T>) => {
  // Extract unique article IDs from all positions
  const articleIds = useMemo(() => {
    const ids = positions
      .map((position) => position.articleId)
      .filter(Boolean) as number[];
    return ids.map(String);
  }, [positions]);

  // Prepare the data request to fetch article information including prices
  const articleRequest = useMemo<DataRequest>(
    () => ({
      entity: Entities.article,
      data: {
        id: null,
        entryPrice: null,
        articlePrices: {
          singlePriceList: [
            {
              id: null,
              priceGroup: { id: null, name: null },
              price: null,
              surcharge: null,
              minuteWage: null,
              isFixedPrice: null,
            },
          ],
          purchasePriceList: [
            {
              id: null,
              supplier: {
                id: null,
                number: null,
                name: null,
              },
              supplierId: null,
              factor: null,
              validFromDate: null,
              listPrice: null,
              discount: null,
              purchasePrice: null,
              supplierText: null,
              orderAmount: null,
              deliveryTime: null,
              articleId: null,
              articleNumber: null,
              currency: {
                id: null,
                name: null,
              },
              note: null,
              unitId: null,
              unit: {
                id: null,
                name: null,
                factor: null,
              },
              amountPerUnit: null,
            },
          ],
        },
        discountable: null,
        standardSupplierId: null,
        standardImageId: null,
        standardPurchasePriceAsEntryPrice: null,
      },
      filter: [
        {
          id: {
            $in: articleIds,
          },
        },
      ],
      skip: !articleIds?.length || groupByType === 'NONE',
    }),
    [articleIds, groupByType]
  );

  // Fetch article data including prices using the data provider
  const providerData = useDataProvider<Article>(articleRequest);

  // Process the positions to enrich them with article prices and discounts
  const applyArticlePricesOnPositions = useCallback(
    (positions: T[]) => {
      // Create a map of articles for faster lookup
      const articleMap = new Map(
        providerData.data.map((article) => [article.id, article])
      );

      // Enrich each position with the corresponding article price and discount
      return positions.map((position) => {
        const article = articleMap.get(position.articleId);

        // Find the specific price for the supplier if available
        const priceBySupplier = article?.articlePrices?.purchasePriceList?.find(
          (x) => x.supplierId === position.supplierId
        );

        const amount = position.amount;

        // Return a new position object with updated price and discount
        return {
          ...position,
          amount,
          singlePriceNet: sourcePrices
            ? position.purchasePrice
            : priceBySupplier?.listPrice || position.purchasePrice || 0,
          discount: sourcePrices ? 0 : priceBySupplier?.discount || 0,
        };
      });
    },
    [providerData.data, sourcePrices]
  );

  return {
    applyArticlePricesOnPositions,
    loading: providerData.loading,
  };
};
